import { AppRoutes } from '@constants/app-routes'
import { OrangeLink } from '@ui/links/OrangeLink'

import {
  DefaultListItem,
  DefaultList,
  ListItem,
  List,
  CustomResult,
} from './styles'

const baseURL = '/images/cases/tour-booking-software-development'

const resultList = [
  'Incorporation of bonus-based tour review and rating options.',
  'Development of a convenient personal account space for booking status, online payments, and user activity monitoring.',
  'Generation of a multi-parameter filter for convenient route planning.',
  'Incorporation of a built-in chat for prompt customer servicing.',
  'Integration of high-quality media content resolution and interactive 3D route maps.',
]

const designList = [
  'UX research',
  'UX strategy building',
  'Wireframing',
  'Visual design',
  'Prototype testing',
]

const codingList = [
  'Test scenarios identification',
  'Test environment setup',
  'Test cases implementation',
  'Result analysis',
  'Retesting',
]

export const config = {
  header: {
    title: 'Online system\nfor tour and\nactivity booking',
    subTitle: 'Safe user-friendly travel and hospitality software solution.',
    illustration: {
      src: `${baseURL}/illustration.webp`,
      alt: 'Online tour booking app',
      title: 'Online tour booking app',
      width: 557,
      height: 591,
    },
    illustrationMobile: {
      src: `${baseURL}/illustrationMobile.webp`,
      alt: 'Online tour booking app',
      title: 'Online tour booking app',
      width: 374,
      height: 484,
    },
    background: {
      src: `${baseURL}/background.webp`,
      alt: 'Online tour booking app',
      title: 'Online tour booking app',
      width: 1440,
      height: 720,
      quality: 100,
    },
  },
  technologies: {
    hexagonImage: {
      src: '/images/empty-hexagon-icon.webp',
      width: 37,
      height: 43,
      alt: '',
    },
    cards: [
      {
        id: 'Challenge',
        title: 'Challenge',
        subtitle:
          'An outdated web application incapable of processing increased masses of customer data due to the recent business expansion to the Asian direction.',
        alt: '',
      },
      {
        id: 'Solution',
        title: 'Solution',
        subtitle:
          'Development of a cross-platform online tour booking app adaptive to web and mobile ecosystems.',
        alt: '',
      },
      {
        id: 'Tech stack',
        title: 'Tech stack',
        subtitle: (
          <>
            <OrangeLink href={AppRoutes.reactNative} inherit target="_self">
              React Native
            </OrangeLink>
            , Detox, Jest,{' '}
            <OrangeLink href={AppRoutes.react} inherit target="_self">
              React JS
            </OrangeLink>
            , Firebase.
          </>
        ),
        alt: '',
      },
    ],
  },
  client: {
    title: 'Client',
    image: {
      src: `${baseURL}/client.webp`,
      alt: 'Online tour booking app interface',
      title: 'Online tour booking app interface',
      width: 998,
      height: 605,
    },
    subtitle:
      'A leading player in the European travel and hospitality industry provides online sightseeing and tour booking services across the EU. Recently, the company has taken a step towards expanding its outreach by focusing on the Asian market. With a view to fundamentally transforming the existing tour booking app, the client reached out to Modsen to hire senior-level cross-platform app developers with extensive domain experience.',
    grayHexagon: {
      src: '/images/big-filled-hexagon.svg',
      alt: '',
      width: 400,
      height: 464,
    },
    orangeHexagon: {
      src: '/images/full-empty-orange-hexagon.webp',
      alt: '',
      width: 400,
      height: 464,
    },
  },
  challenge: {
    title: 'Challenge',
    subtitle: (
      <>
        The key objective of our partner was to qualitatively scale up their
        business and support the expansion by developing a robust, safe, and
        user-friendly cross-platform application running both within mobile and
        web ecosystems to ensure maximum value and convenience. The client’s key
        goal boiled down to uniting thousands of sightseeing activity providers
        within the app and ensuring convenient error-free reservation of desired
        tours across the EU.
        <br />
        <br />
        The aspects projected to have been incorporated into the travel software
        system encompassed the following:
      </>
    ),

    list: [
      'Division and selection of tours and activities based on the geographical principle.',
      'Demonstration of the user reviews as well as tour operators and ratings.',
      'Development of user-friendly personal accounts containing all the information necessary for efficient travel activities planning, booking monitoring, and payment processing.',
      'Incorporation of map-based ratings of popular cities and places to visit.',
      'Insert sections with detailed user reports in the form of an in-app blog.',
    ],
  },
  team: {
    title: 'Team',
    teamMembers: [
      {
        amount: 1,
        role: 'Project Manager',
      },
      {
        amount: 1,
        role: 'Team Lead',
      },
      {
        amount: 3,
        role: 'QA testers',
      },
      {
        amount: 1,
        role: 'Business Analyst',
      },
      {
        amount: 6,
        role: 'Developer Engineers',
      },
    ],
    teamImage: {
      src: `${baseURL}/team.webp`,
      alt: 'Front-end software engineer',
      title: 'Front-end software engineer',
      width: 643,
      height: 429,
    },
  },
  development: {
    title: 'Development process',
    rightHexagon: {
      width: 363,
      height: 363,
      src: '/images/full-empty-orange-hexagon.webp',
      alt: '',
    },
    stages: [
      {
        id: 1,
        title: 'Requirements gathering and processing',
        text: (
          <>
            The discovery phase lays the foundation of every project being
            developed by Modsen experts. To ensure maximum compliance with our
            partner’s requirements and expectations, we place great emphasis on
            conducting internal and external research followed by several online
            meetups with the client.
            <br />
            <br />
            Having a proven track record of building travel and hospitality
            software solutions for 5+ years, Modsen senior PM, BA, and CTO
            managed to cut the requirements gathering phase to 2 calls due to
            close familiarity with the specificity of the given project and
            thanks to obtaining a thorough realistic product requirements plan
            drafted by our partner.
            <br />
            <br />
            The stage closed with submitting a final document supplemented by
            Modsen commentaries and alterations to the client and waiting for
            their approval.
          </>
        ),
      },
      {
        id: 2,
        title: 'Planning',
        text: (
          <>
            Unwavering compliance with deadlines and well-oiled project
            management processes are at the core of Modsen professional
            approach. To properly plan out the travel app development keeping in
            mind our key priorities, we turned to{' '}
            <OrangeLink href={AppRoutes.approach} inherit target="_self">
              Agile methodologies
            </OrangeLink>{' '}
            and tapped into the extensive experience and best practices we apply
            to build software products proactively.
            <br />
            <br />
            As a result of close cooperation between relevant Modsen
            specialists, a comprehensive project development plan was scripted
            outlining the key milestones, sprints, responsibilities, resource
            allocation, and other significant details. The draft was then
            submitted to our partner and after minor modifications, the team was
            given the green light to get down to code-cracking.
          </>
        ),
      },
      {
        id: 3,
        title: 'Team assembly',
        text: (
          <>
            We consider each software development project that comes into our
            hands as a lock that requires the right key to be opened and achieve
            its full potential. To that end, at Modsen we have built a
            multistage system of specialists’ filtration, upgrading, and testing
            to entrust product building to truly dedicated, challenge-loving
            domain experts. When selecting developer engineers for the tour
            booking cross-platform software development project, our CTO focused
            not only on their proficiency in the required tech stack but mainly
            on the{' '}
            <OrangeLink href={AppRoutes.travel} inherit target="_self">
              travel and hospitality product-building expertise
            </OrangeLink>{' '}
            under their belts.
            <br />
            <br />
            CTO-based team assembly at Modsen was combined with engaging our
            partner in the candidates’ competence verification process to ensure
            that the project is handled by dedicated skillful professionals with
            relevant domain experience.
          </>
        ),
      },

      {
        id: 4,
        title: 'Design',
        text: (
          <>
            Tasked with building a layout of the web and mobile versions of the
            tour and activity booking platform,{' '}
            <OrangeLink href={AppRoutes.designStudio} inherit target="_self">
              Modsen Design Studio UI/UX experts{' '}
            </OrangeLink>
            centered their efforts around creating a visually appealing,
            functional, and at the same time navigable, and intuitive interface
            that would facilitate vacation planning and aid customers in finding
            the best sightseeing and activity options.
            <br />
            <br />
            Our team reworked the key design elements by adding more
            distinguishable and recognizable app front-end components. The full
            cycle of the design process was divided into 5 core steps: The
            complete cycle of the design process was divided into 5 core steps:
            <List>
              {designList.map(task => (
                <ListItem key={task}>{task}</ListItem>
              ))}
            </List>
            Reaching the key UI/UX implementation objectives, Modsen designers
            handed software prototypes over to the developer team to power the
            impeccable prototypes with the back end.
          </>
        ),
      },
      {
        id: 5,
        title: 'Coding',
        text: (
          <>
            Adherence to constant competence improvement and efficient
            incorporation of the most advanced technologies in cross-platform
            app development results in implementing progressive tech stack tools
            that are the best fit for the product under development.
            <br />
            <br />
            Modsen project team working on the creation of the online tour
            booking app enjoyed 5+ years of experience in crafting similar
            solutions and were well aware of the pitfalls such type of software
            product development conceals.
            <br />
            <br />6 senior-level coding enthusiasts employed tried-and-tested
            programming methodologies as well as innovative tech practices to
            achieve the ultimate results that would drive our partner’s business
            transformation.
          </>
        ),
      },
      {
        id: 6,
        title: 'Comprehensive testing',
        text: (
          <>
            When the code was up and running,{' '}
            <OrangeLink
              href={AppRoutes.qualityAssurance}
              inherit
              target="_self"
            >
              Modsen QA specialists
            </OrangeLink>{' '}
            started the process of meticulous software quality verification to
            identify the slightest inefficiencies and have them fixed.
            <br />
            <br />
            The end-to-end cross-platform app testing cycle encompassed the
            following stages:
            <DefaultList>
              {codingList.map(task => (
                <DefaultListItem key={task}>{task}</DefaultListItem>
              ))}
            </DefaultList>
          </>
        ),
      },
      {
        id: 7,
        title: 'Integration',
        text: (
          <>
            Having a robust, safe, and scalable application at hand, the team
            got the technical documentation and user guidelines ready to be
            transferred to the client. Modsen experts also assisted our partner
            during the software deployment and consulted the product owner about
            the most efficient and beneficial ways of future app exploitation.
          </>
        ),
      },
      {
        id: 8,
        title: 'Servicing and further cooperation',
        text: (
          <>
            After handing the product over to our partner, we didn’t leave them
            one on one for long. Software development is a process of continuous
            improvement, upgrading, and upscaling of the initial project with a
            view to perfect efficiency, add new functionality, and broaden
            business horizons.
            <br />
            <br />
            The long-term cooperation between{' '}
            <OrangeLink href={AppRoutes.partnership} inherit target="_self">
              Modsen team
            </OrangeLink>{' '}
            and its partner keeps bearing fruit in the form of more unique,
            technological, and user-convenient solutions incorporated into the
            tour booking application. To date, we’re working on the app
            internationalization and broadening the sightseeing tour providers
            database.
          </>
        ),
      },
    ],
    image: {
      src: `${baseURL}/development.webp`,
      alt: 'Tour booking app',
      title: 'Tour booking app',
      width: 999,
      height: 582,
    },
  },
  result: {
    text: (
      <>
        The well-adjusted in-house processes coupled with the extensive domain
        expertise of the project team allowed us to launch the online tour
        booking system within the preset deadlines. A range of the tasks
        implemented during the development process included:
        <List>
          {resultList.map(task => (
            <ListItem key={task}>{task}</ListItem>
          ))}
        </List>
        Modsen-designed online sightseeing and tour booking platform has been
        successfully operating in the European market for 1.5 years and showing
        outstanding results driving our partner’s business forward.
      </>
    ),
    grayHexagon: {
      width: 363,
      height: 363,
      src: `${baseURL}/svg/grayHexagon.svg`,
      alt: '',
    },
    title: 'Result',
    cards: [
      {
        id: 1,
        title: '34%',
        header: 'h3',
        subtitle:
          'increased user engagement rate due to tapping into the Chinese tourist potential',
      },
      {
        id: 2,
        title: (
          <>
            <CustomResult>inclusion into</CustomResult> top 15
          </>
        ),
        header: 'h3',
        subtitle: 'travel and hospitality apps in AppStore ratings',
      },
      {
        id: 3,
        title: '28%',
        header: 'h3',
        subtitle:
          'yearly revenue growth compared to the previous financial year',
      },
    ],
  },
}
